import { Link, type LinkProps } from '@chakra-ui/react'

type BtnVariant = 'primary' | 'secondary' | 'ground' | 'groundOutline'

interface BtnProps extends Omit<LinkProps, 'variant'> {
  children: React.ReactNode
  href: string
  variant?: BtnVariant
  /** Full-width bar with the label flush left, per the design system's `.btn-block`. */
  block?: boolean
}

const VARIANT_STYLES: Record<BtnVariant, LinkProps> = {
  primary: {
    bg: 'accent',
    color: 'ground',
    _hover: { bg: 'accent.600' },
    _active: { bg: 'accent.700' },
  },
  secondary: {
    borderColor: 'divider',
    color: 'ink',
    _hover: { bg: 'rgba(32, 30, 29, 0.07)' },
    _active: { bg: 'rgba(32, 30, 29, 0.14)' },
  },
  /** Ground-filled button sitting on the accent field. */
  ground: {
    bg: 'ground',
    color: 'ink',
  },
  /** Ground-outlined transparent button sitting on the accent field. */
  groundOutline: {
    borderColor: 'ground',
    color: 'ground',
  },
}

/** Anchor styled as the design system's `.btn`: Archivo 800, square corners, no underline. */
export const Btn = ({ children, href, variant = 'primary', block = false, ...props }: BtnProps) => (
  <Link
    href={href}
    display="inline-flex"
    alignItems="center"
    gap="6px"
    fontFamily="heading"
    fontWeight="800"
    fontSize="14px"
    lineHeight="1.2"
    textDecoration="none"
    borderRadius="0"
    border="1px solid transparent"
    px="14px"
    py="8px"
    _focusVisible={{ outline: '2px solid', outlineColor: 'accent', outlineOffset: '2px' }}
    {...VARIANT_STYLES[variant]}
    {...(block ? { width: '100%', justifyContent: 'flex-start', textAlign: 'left' as const } : {})}
    {...props}
  >
    {children}
  </Link>
)
